import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useAuth } from '../state/AuthContext';
import { Logo } from './Logo';

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container navbar-inner">
        <Link to="/" className="brand">
          <Logo size={28} />
          PermitGuard AI
        </Link>
        <nav className="row gap-4" style={{ marginLeft: 8 }}>
          <NavLink to="/how-it-works" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>How It Works</NavLink>
          <NavLink to="/faq" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>FAQ</NavLink>
          <a href="https://sensifai.com" target="_blank" rel="noreferrer" className="nav-link row gap-1">
            Sensifai <ExternalLink size={14} />
          </a>
        </nav>
        <div className="spacer" />
        {user ? (
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/app')}>
            Open Dashboard <ArrowRight size={16} />
          </button>
        ) : (
          <div className="row gap-2">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/login')}>Sign in</button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/login')}>Try the demo</button>
          </div>
        )}
      </div>
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer style={{ background: 'var(--navy)', color: 'var(--text-on-dark)', padding: '64px 0 32px' }}>
      <div className="container">
        <div className="grid-4" style={{ marginBottom: 48 }}>
          <div className="stack gap-2">
            <div className="row gap-1" style={{ fontWeight: 800, fontSize: '1.0625rem' }}>
              <Logo size={22} /> PermitGuard AI
            </div>
            <p className="body-small" style={{ color: '#a9b6cc', maxWidth: 260 }}>
              Deterministic, fail-closed Safe Work Permit &amp; LOTO conflict detection for hazardous
              industrial facilities. Part of the Sensifai Industrial AI Products family.
            </p>
          </div>
          <FooterColumn
            title="Product"
            links={[
              { label: 'How It Works', to: '/how-it-works' },
              { label: 'FAQ', to: '/faq' },
              { label: 'Sign in / Demo', to: '/login' },
            ]}
          />
          <FooterColumn
            title="Company"
            links={[{ label: 'Sensifai.com', href: 'https://sensifai.com' }]}
          />
          <div className="stack gap-2">
            <div className="eyebrow" style={{ color: '#a9b6cc' }}>Safety notice</div>
            <p className="body-small" style={{ color: '#a9b6cc' }}>
              AI components in this product are non-authoritative. All isolation/override decisions
              remain deterministic and human-approved, per our published Safety Architecture.
            </p>
          </div>
        </div>
        <div className="divider" style={{ background: 'rgba(255,255,255,0.12)', marginBottom: 24 }} />
        <div className="row" style={{ color: '#a9b6cc', fontSize: '0.8125rem' }}>
          <span>© {new Date().getFullYear()} Sensifai Industrial AI Products. All rights reserved.</span>
          <div className="spacer" />
          <a href="https://sensifai.com" target="_blank" rel="noreferrer" className="row gap-1" style={{ color: '#a9b6cc' }}>
            sensifai.com <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }: { title: string; links: Array<{ label: string; to?: string; href?: string }> }) {
  return (
    <div className="stack gap-2">
      <div className="eyebrow" style={{ color: '#a9b6cc' }}>{title}</div>
      <ul className="stack gap-2">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link to={l.to} className="body-small" style={{ color: '#cfd9e8' }}>{l.label}</Link>
            ) : (
              <a href={l.href} target="_blank" rel="noreferrer" className="body-small" style={{ color: '#cfd9e8' }}>{l.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
